import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CLAÈ Admin — FAQs CMS.
 *   • Add / edit / delete FAQs
 *   • Toggle visibility
 *   • Move up / down to reorder (persists to backend)
 * Public /faqs page reads the same collection via GET /api/faqs.
 */
export default function AdminFAQs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row object or {} for new
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/faqs");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load FAQs");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    const q = (editing.question || "").trim();
    const a = (editing.answer || "").trim();
    if (!q || !a) { toast.error("Both question and answer are required"); return; }
    setSavingId(editing.id || "new");
    try {
      if (editing.id) {
        await api.patch(`/admin/faqs/${editing.id}`, { question: q, answer: a, visible: editing.visible });
      } else {
        await api.post("/admin/faqs", { question: q, answer: a, visible: editing.visible !== false });
      }
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSavingId(null);
  };

  const del = async (row) => {
    if (!window.confirm(`Delete FAQ: "${row.question}"?`)) return;
    try {
      await api.delete(`/admin/faqs/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleVisible = async (row) => {
    try {
      await api.patch(`/admin/faqs/${row.id}`, { visible: !row.visible });
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const move = async (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= rows.length) return;
    const copy = [...rows];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setRows(copy);
    try {
      await api.post("/admin/faqs/reorder", { ids: copy.map((r) => r.id) });
    } catch (e) {
      toast.error("Failed to reorder");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">FAQs</h2>
          <p className="text-xs text-stone-600 mt-1">Edit the questions &amp; answers shown on the public /faqs page.</p>
        </div>
        <button
          data-testid="faq-add-btn"
          onClick={() => setEditing({ question: "", answer: "", visible: true })}
          className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 transition"
        >+ Add FAQ</button>
      </div>

      {loading && <div className="text-sm text-stone-500">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-stone-500">No FAQs yet. Click “+ Add FAQ”.</div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.id} className="border hairline bg-white p-4" data-testid={`faq-row-${r.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`faq-up-${r.id}`} onClick={() => move(i, -1)} disabled={i===0}
                  className="mono-label text-[11px] border border-stone-300 px-2 py-1 disabled:opacity-30 hover:bg-stone-100">↑</button>
                <button data-testid={`faq-down-${r.id}`} onClick={() => move(i, 1)} disabled={i===rows.length-1}
                  className="mono-label text-[11px] border border-stone-300 px-2 py-1 disabled:opacity-30 hover:bg-stone-100">↓</button>
              </div>
              <div className="flex-1">
                <p className="font-medium text-stone-900 text-sm">{r.question}</p>
                <p className="text-xs text-stone-600 mt-1 whitespace-pre-wrap">{r.answer}</p>
                {!r.visible && (
                  <span className="inline-block mt-2 mono-label text-[10px] text-amber-700">HIDDEN FROM PUBLIC</span>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={`faq-toggle-${r.id}`} onClick={() => toggleVisible(r)}
                  className="mono-label text-[11px] border border-stone-400 text-stone-700 px-3 py-1 hover:bg-stone-100">
                  {r.visible ? "Hide" : "Show"}
                </button>
                <button data-testid={`faq-edit-${r.id}`} onClick={() => setEditing({ ...r })}
                  className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition">Edit</button>
                <button data-testid={`faq-delete-${r.id}`} onClick={() => del(r)}
                  className="mono-label text-[11px] border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" onClick={() => setEditing(null)} data-testid="faq-editor-modal">
          <div className="bg-white border hairline w-full max-w-2xl mt-10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-stone-900">{editing.id ? "Edit FAQ" : "New FAQ"}</h3>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mono-label text-[11px] text-stone-600 block mb-1">QUESTION</span>
                <input data-testid="faq-field-question" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                  className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="block">
                <span className="mono-label text-[11px] text-stone-600 block mb-1">ANSWER</span>
                <textarea data-testid="faq-field-answer" rows={5} value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                  className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" data-testid="faq-field-visible" checked={editing.visible !== false} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} />
                Visible on public FAQ page
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setEditing(null)} className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100">Cancel</button>
              <button data-testid="faq-save-btn" disabled={!!savingId} onClick={save} className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 disabled:opacity-50 transition">
                {savingId ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
